import {
	Box,
	Button,
	FormControl,
	Grid,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSettings } from "../../../hooks/useSettings";
import { useGetCurrentUserQuery } from "../../user/userApiSlice";
import { useSaveAIRulesMutation } from "../cargoApiSlice";

export const AIRulesForm = () => {
	const { t } = useTranslation();
	const { settings } = useSettings();
	const { enqueueSnackbar } = useSnackbar();
	const { data: user } = useGetCurrentUserQuery(undefined);

	const [saveRules, { isLoading }] = useSaveAIRulesMutation();

	const {
		handleSubmit,
		control,
		formState: { errors },
		setError,
		setValue,
		watch,
	} = useForm({
		// resolver: yupResolver(formSchema),
		shouldFocusError: true,
		defaultValues: user?.preferences?.aiRules,
	});

	useEffect(() => {
		if (Object.keys(errors).length > 0) {
			Object.values(errors).forEach((err) => {
				enqueueSnackbar(err.message, {
					variant: "error",
					preventDuplicate: true,
				});
			});
		}
	}, [errors, enqueueSnackbar]);

	const watchVehicleTypeId = watch("vehicleTypeId");
	const watchVehicleBodyId = watch("vehicleBodyId");
	const [vehicleBodyIdOptions, setVehicleBodyIdOptions] = useState([]);
	useEffect(() => {
		if (watchVehicleTypeId && watchVehicleTypeId.length > 0) {
			if (watchVehicleTypeId.length > 1) {
				setVehicleBodyIdOptions(
					settings.vehicleOptions.vehicleTypesOptions["ANY_TYPE"].bodyTypes
				);
			} else {
				setVehicleBodyIdOptions(
					settings.vehicleOptions.vehicleTypesOptions[watchVehicleTypeId]
						.bodyTypes
				);
			}

			if (watchVehicleBodyId) {
				let isVehicleBodyInOptions = false;

				if (watchVehicleTypeId.length > 1) {
					isVehicleBodyInOptions = watchVehicleBodyId.every((id) => {
						return Object.keys(
							settings.vehicleOptions.vehicleTypesOptions["ANY_TYPE"].bodyTypes
						).includes(id);
					});
				} else {
					isVehicleBodyInOptions = watchVehicleBodyId.every((id) => {
						return Object.keys(
							settings.vehicleOptions.vehicleTypesOptions[watchVehicleTypeId]
								.bodyTypes
						).includes(id);
					});
				}

				if (!isVehicleBodyInOptions) {
					setValue("vehicleBodyId", []);
				}
			}
		}
	}, [watchVehicleTypeId]);

	const handleSave = async (data) => {
		await saveRules({ data: { rules: data } })
			.unwrap()
			.then(() => {
				enqueueSnackbar(t("saved_ai_rules"), {
					variant: "success",
				});
			})
			.catch((error) => {
				if (error?.data?.errors) {
					const errors = error.data.errors;
					Object.entries(errors).forEach(([key, message]) => {
						setError(key);
						enqueueSnackbar(message[0], {
							variant: "error",
							preventDuplicate: true,
						});
					});
				} else {
					enqueueSnackbar(error?.data?.message || t("default_error"), {
						variant: "error",
						preventDuplicate: true,
					});
				}
			});
	};

	return (
		<Box component="form" noValidate onSubmit={handleSubmit(handleSave)}>
			<Grid container spacing={1.5}>
				<Grid size={12}>
					<Grid container rowSpacing={1.5} columnSpacing={3}>
						<Grid size={6}>
							<Controller
								control={control}
								name="ftl"
								render={({ field: { onChange, value } }) => {
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<label htmlFor="ftl" style={{ fontWeight: 700 }}>
												FTL / LTL
											</label>
											<FormControl fullWidth variant="outlined" size="small">
												<Select
													displayEmpty
													id="ftl"
													inputProps={{
														id: "ftl",
													}}
													value={value}
													onChange={onChange}
													error={!!errors?.smtpEncryption}
												>
													{[
														{ value: "", label: t("leave_empty") },
														{ value: "ftl", label: "FTL" },
														{ value: "ltl", label: "LTL" },
													].map((option) => (
														<MenuItem key={option.value} value={option.value}>
															{option.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Box>
									);
								}}
							/>
						</Grid>
						<Grid size={6}>
							<Controller
								control={control}
								name="vehicleTypeId"
								render={({ field: { onChange, value } }) => {
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<label
												htmlFor="vehicleTypeId"
												style={{ fontWeight: 700 }}
											>
												{t("vehicle_type_label")}
											</label>
											<FormControl fullWidth variant="outlined" size="small">
												<Select
													multiple
													displayEmpty
													id="vehicleTypeId"
													inputProps={{ id: "vehicleTypeId" }}
													value={value}
													onChange={onChange}
													error={!!errors?.smtpEncryption}
													renderValue={(selected) => {
														if (selected.length === 0) {
															return t("leave_empty");
														}

														return selected
															.map(
																(s) =>
																	settings.vehicleOptions.vehicleTypesOptions[s]
																		.name
															)
															.join(", ");
													}}
												>
													{Object.entries(
														settings.vehicleOptions.vehicleTypesOptions
													)
														.filter(([value, _label]) => {
															return value !== "ANY_TYPE";
														})
														.map(([value, label]) => {
															return { value, label: label.name };
														})
														.map((option) => (
															<MenuItem key={option.value} value={option.value}>
																{option.label}
															</MenuItem>
														))}
												</Select>
											</FormControl>
										</Box>
									);
								}}
							/>
						</Grid>
						<Grid size={6}>
							<Controller
								control={control}
								name="vehicleBodyId"
								render={({ field: { onChange, value } }) => {
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<label
												htmlFor="vehicleBodyId"
												style={{ fontWeight: 700 }}
											>
												{t("vehicle_body_label")}
											</label>
											<FormControl fullWidth variant="outlined" size="small">
												<Select
													multiple
													displayEmpty
													disabled={
														!watchVehicleTypeId ||
														watchVehicleTypeId?.length < 1
													}
													id="vehicleBodyId"
													inputProps={{ id: "vehicleBodyId" }}
													value={value}
													onChange={onChange}
													error={!!errors?.smtpEncryption}
													renderValue={(selected) => {
														if (selected.length === 0) {
															return t("leave_empty");
														}

														return selected
															.map((s) => vehicleBodyIdOptions[s])
															.join(", ");
													}}
												>
													{Object.entries(vehicleBodyIdOptions)
														.map(([value, label]) => {
															// allow only 5 options to be selected
															const shouldDisable =
																watchVehicleBodyId &&
																watchVehicleBodyId.length > 4 &&
																!watchVehicleBodyId.includes(value);

															return { value, label, disabled: shouldDisable };
														})
														.map((option) => (
															<MenuItem key={option.value} value={option.value}>
																{option.label}
															</MenuItem>
														))}
												</Select>
											</FormControl>
										</Box>
									);
								}}
							/>
						</Grid>
						<Grid size={6}>
							<Controller
								control={control}
								name="times"
								render={({ field: { onChange, value } }) => {
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<label htmlFor="times" style={{ fontWeight: 700 }}>
												{t("unload_load_hours")}
											</label>
											<FormControl fullWidth variant="outlined" size="small">
												<Select
													displayEmpty
													id="times"
													inputProps={{ id: "times" }}
													value={value}
													onChange={onChange}
													error={!!errors?.smtpEncryption}
												>
													{[
														{
															value: "",
															label: t("leave_empty_fill_manually"),
														},
														{
															value: "profile-defaults",
															label: t("use_hours_set_in_my_account"),
														},
													].map((option) => (
														<MenuItem key={option.value} value={option.value}>
															{option.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Box>
									);
								}}
							/>
						</Grid>
						<Grid size={6}>
							<Controller
								control={control}
								name="weight"
								render={({ field: { onChange, value } }) => (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										<label htmlFor="weight" style={{ fontWeight: 700 }}>
											{t("weight")}
										</label>
										<TextField
											size="small"
											value={value}
											variant="outlined"
											placeholder={t("leave_empty")}
											fullWidth
											onChange={onChange}
											error={!!errors?.smtpEncryption}
											slotProps={{
												input: {
													endAdornment: "t",
												},

												htmlInput: { id: "weight" },
											}}
										/>
									</Box>
								)}
							/>
						</Grid>
						<Grid size={6}>
							<Controller
								control={control}
								name="length"
								render={({ field: { onChange, value } }) => (
									<Box sx={{ display: "flex", flexDirection: "column" }}>
										<label htmlFor="length" style={{ fontWeight: 700 }}>
											{t("length")}
										</label>
										<TextField
											size="small"
											value={value}
											variant="outlined"
											placeholder={t("leave_empty")}
											fullWidth
											onChange={onChange}
											error={!!errors?.smtpEncryption}
											slotProps={{
												input: {
													endAdornment: "m",
												},

												htmlInput: { id: "length" },
											}}
										/>
									</Box>
								)}
							/>
						</Grid>
						<Grid size={12}>
							<Controller
								control={control}
								name="loadingDate"
								render={({ field: { onChange, value } }) => {
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<label htmlFor="loadingDate" style={{ fontWeight: 700 }}>
												{t("load_date")}
											</label>
											<FormControl fullWidth variant="outlined" size="small">
												<Select
													displayEmpty
													id="loadingDate"
													inputProps={{ id: "loadingDate" }}
													value={value}
													onChange={onChange}
													error={!!errors?.smtpEncryption}
												>
													{[
														{
															value: "",
															label: t("leave_empty"),
														},
														{
															value: "current-day",
															label: t("insert_current_day"),
														},
														{
															value: "next-day",
															label: t("insert_next_day"),
														},
														{
															value: "range-today-1",
															label: t("insert_range_today_plus_1"),
														},
														{
															value: "range-today-2",
															label: t("insert_range_today_plus_2"),
														},
														{
															value: "range-tomorrow-1",
															label: t("insert_range_tomorrow_plus_1"),
														},
														{
															value: "range-tomorrow-2",
															label: t("insert_range_tomorrow_plus_2"),
														},
													].map((option) => (
														<MenuItem key={option.value} value={option.value}>
															{option.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Box>
									);
								}}
							/>
						</Grid>
						{/* <Grid item xs={12}>
							<Controller
								control={control}
								name="unloadingDate"
								render={({ field: { onChange, value } }) => {
									return (
										<Box sx={{ display: "flex", flexDirection: "column" }}>
											<label htmlFor="loadingDate" style={{ fontWeight: 700 }}>
												{t("unload_date")}
											</label>
											<FormControl fullWidth variant="outlined" size="small">
												<Select
													displayEmpty
													id="loadingDate"
													inputProps={{ id: "loadingDate" }}
													value={value}
													onChange={onChange}
													error={!!errors?.smtpEncryption}
												>
													{[
														{
															value: "",
															label: t("leave_empty"),
														},
														{
															value: "current-day",
															label: t("insert_current_day"),
														},
														{
															value: "next-day",
															label: t("insert_next_day"),
														},
														{
															value: "range-today-1",
															label: t("insert_range_today_plus_1"),
														},
														{
															value: "range-today-2",
															label: t("insert_range_today_plus_2"),
														},
														{
															value: "range-tomorrow-1",
															label: t("insert_range_tomorrow_plus_1"),
														},
														{
															value: "range-tomorrow-2",
															label: t("insert_range_tomorrow_plus_2"),
														},
													].map((option) => (
														<MenuItem key={option.value} value={option.value}>
															{option.label}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</Box>
									);
								}}
							/>
						</Grid> */}
						<Grid size={12}>
							<Box sx={{ display: "flex", justifyContent: "end", mt: "10px" }}>
								<Button loading={isLoading} type="submit" variant="contained">
									{t("save")}
								</Button>
							</Box>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Box>
	);
};
